"use client";
import { noto_sans } from '@/globalConstants';
import { ReactElement } from 'react';

const Policy = () => {

    const sectionTitle = (text: string) => (
        <h2 className='font-semibold text-[1.5rem] text-[#1D4749]'>{text}</h2>
    );

    const sectionParagraph = (text: string) => (
        <p>{text}</p>
    );

    const listItem = (heading: string, description: string | ReactElement) => (
        <li>
            <span className="font-semibold">{heading}</span>
            <span>: {description}</span>
        </li>
    );

    return (
        <section className={`app-container ${noto_sans.className}`}>
            <div className="bg-[#ffffff] text-[#565151] py-10 text-[1.25rem] flex items-center justify-center">
                <div className='md:w-[80%] xl:w-[70%]'>

                    <div className='p-5'>
                        <h1 className="text-center text-[3rem] text-[#1D4749] font-[600]">
                            Dharmayana App - Privacy Policy
                        </h1>
                    </div>

                    <div className='policy-content p-5 grid gap-4'>

                        {sectionTitle('Privacy Policy for Dharmayana')}
                        {sectionParagraph('Last Updated: 15th October 2026')}

                        {sectionTitle('1. Introduction')}
                        {sectionParagraph(`Dharmayana is committed to protecting your privacy. This privacy policy explains our practices regarding the collection, use, and disclosure of information that does not personally identify you.`)}

                        {sectionTitle('2. Information We Collect')}
                        <div className='px-8'>
                            <ul className='list-disc grid gap-3 ml-2'>

                                {listItem('Personally Identifiable Information',
                                    <span>
                                        We collect certain Personally Identifiable Information (PII) from you such as your name, phone number, gender, and date of birth when you create an account and login into our app. This information is necessary for providing personalized services, creating your profile, and enhancing your user experience.
                                        <b> No PII is collected if you do not login into the app.</b>
                                    </span>
                                )}

                                {listItem('App Usage Data',
                                    'We collect anonymous information about how you use the app, including interactions with the app interface, preferences, and settings.'
                                )}

                                {listItem('Crash Reports',
                                    'We gather data on app crashes to improve user experience and app stability.'
                                )}

                                {listItem('Session Replay (Android)',
                                    'On Android, we use Mixpanel Session Replay, which records the app screens, to fix bugs and improve the app. Sensitive information such as OTPs and payment details is hidden. Recordings are kept for a short period (30 days) and automatically removed post that.'
                                )}

                                {listItem('Coarse Location Data',
                                    'We collect coarse location data to provide location-based services and improve app functionality.'
                                )}

                                {listItem('Marketing SDKs',
                                    'We use 3rd party marketing tools to measure the effectiveness of our Ad campaigns. However, these do not collect any Personally Identifiable Information (PII) from our users.'
                                )}

                            </ul>
                        </div>

                        {sectionParagraph(`The following points apply specifically to the Ayurvedic Consultation feature.`)}
                        <div className='px-8'>
                            <ul className='list-disc grid gap-3 ml-2'>

                                {listItem('Ayurvedic Consultation Intake Data',
                                    'When you book an Ayurvedic Consultation, we collect your Name, Age, Gender, and Date of Birth as part of the consultation intake form. This information is collected solely to facilitate your consultation with the assigned Ayurvedic doctor and is not used for any other purpose.'
                                )}

                                {listItem('Consultation e-Remedy Data',
                                    'Post-session, we store the remedy recommendations issued by your Ayurvedic doctor against your order. This data is accessible only to you and the doctor who issued it.'
                                )}

                            </ul>
                        </div>

                        {sectionParagraph(`The following points apply specifically to the "Talk to a Dharmik Counsellor" feature.`)}
                        <div className='px-8'>
                            <ul className='list-disc grid gap-3 ml-2'>

                                {listItem('Consultation Intake Data (Talk to a Dharmik Counsellor)',
                                    'When you book a counselling session, we collect your Name, Age, Gender, and Date of Birth from the intake form. This is used solely to facilitate your session with the assigned counsellor.'
                                )}

                                {listItem('Session Notes',
                                    'Post-session recommendations issued by your counsellor (Prarthana, Dhyana, and Other Recommendations) are stored against your order. This data is accessible only to you and the counsellor who issued it.'
                                )}

                            </ul>
                        </div>

                        {sectionTitle('3. How We Use Your Information')}
                        {sectionParagraph(`The information collected is used for personalizing the app services for our logged-in users and improving the app experience and functionality. It helps us identify and fix technical issues, understand user engagement, and optimize our services.`)}

                        {sectionParagraph(`Additional use for Ayurvedic Consultation:`)}
                        <div className='px-8'>
                            <ul className='list-disc grid gap-3 ml-2'>
                                {listItem('Ayurvedic Consultation Usage',
                                    'Consultation intake data (name, age, gender, DOB) is shared with the assigned Ayurvedic doctor solely to facilitate your session. It is not used for marketing, profiling, or shared with any third party.'
                                )}
                            </ul>
                        </div>

                        {sectionTitle('4. Data Sharing and Disclosure')}
                        {sectionParagraph('We do not share, sell, or rent your data to third parties. The data collected is solely for internal use.')}

                        {sectionParagraph(`Exception for Ayurvedic Consultation:`)}
                        <div className='px-8'>
                            <ul className='list-disc grid gap-3 ml-2'>

                                {listItem('Doctor Access',
                                    'Your consultation intake information is shared with the Ayurvedic doctor assigned to your session. The doctor can view your name, age, gender, DOB, and past consultation questions from sessions they personally handled. They cannot view data from consultations handled by other doctors.'
                                )}

                                {listItem('Customer Support Access',
                                    'Customer Support can view your order status, booking details, and payment information for Ayurvedic Consultation orders. Customer Support cannot view your session content or e-Remedy details.'
                                )}

                                {listItem('Exception — Talk to a Dharmik Counsellor',
                                    'Your intake information (name, age, gender, date of birth) is shared with the counsellor assigned to your session solely to facilitate the consultation. The counsellor can also view past consultation questions from sessions they personally handled. No session data is visible to other counsellors, Customer Support, or any third party.'
                                )}

                            </ul>
                        </div>

                        {sectionTitle('5. Ayurvedic Consultation — Session Confidentiality')}
                        {sectionParagraph('Dharmayana treats your Ayurvedic consultation with complete confidentiality. Your session details are private and protected as follows:')}

                        <div className='px-8'>
                            <ul className='list-disc grid gap-3 ml-2'>

                                {listItem('No Recording',
                                    'Sessions are not recorded. No audio or video recording of your consultation is made or stored by Dharmayana.'
                                )}

                                {listItem('Restricted Visibility',
                                    'Your session content is not visible to Employees of Dharmayana, or management, except in cases required by law.'
                                )}

                                {listItem('e-Remedy Visibility',
                                    'e-Remedy recommendations (Prarthana, Dhyana, Other Recommendations) are visible only to you in the Dharmayana app and to the doctor who issued them in the Mitra app.'
                                )}

                                {listItem('Doctor Isolation',
                                    'Other doctors cannot view your session history or e-Remedy from consultations handled by a different doctor.'
                                )}

                                {listItem('Legal Exceptions',
                                    'Confidentiality may be overridden only if: (a) you disclose an immediate risk of harm to yourself or others, as required by Indian law; or (b) Dharmayana receives a valid legal order from a court or government authority.'
                                )}

                            </ul>
                        </div>

                        {sectionTitle('6. Session Confidentiality — Talk to a Dharmik Counsellor')}
                        {sectionParagraph(`Your counselling session is completely confidential. Dharmayana does not record audio or video of any session. Session content is not accessible to Dharmayana Employees or management. Customer Support can view your order and payment status only — not your session content or recommendations. Confidentiality will only be overridden in the following circumstances:`)}

                        <div className='px-8'>
                            <ul className='list-disc grid gap-3 ml-2'>
                                <li><span>You disclose an immediate risk of harm to yourself or others, as required under Indian law</span></li>
                                <li><span>Dharmayana receives a valid legal order from a court or government authority</span></li>
                            </ul>
                        </div>

                        {sectionTitle('7. Data Security')}
                        {sectionParagraph('We implement reasonable measures to protect your data from unauthorized access, alteration, or destruction.')}

                        {sectionTitle('8. Email Communications')}
                        {sectionParagraph('If you add your email address to your Dharmayana profile, we may send you occasional emails about Hindu festivals, rituals, traditions and spiritual content, along with updates about Dharmayana. These emails are sent from @dharmayana.io. Every such email includes an unsubscribe link, and you can opt out at any time. Once you unsubscribe, we will stop sending you these emails. Opting out does not affect important emails about your bookings or account.')}

                        {sectionTitle('9. Changes to this Privacy Policy')}
                        {sectionParagraph('We may update this policy from time to time. We encourage you to review this policy periodically.')}

                        {sectionTitle('10. Contact Us')}
                        {sectionParagraph('If you have questions about this privacy policy, please contact us at Dharmayana-feedback.')}
                    </div>
                </div>
            </div>
        </section>
    );
}

export default Policy;
